/**
 * contingencyNoticesAgent.js — every API call the Wisconsin Contingency Hub
 * makes, in one place.
 *
 * Contract: docs/architecture/contingency-tracking.md §5.1, as built in
 * backend/app/routes/contingency_notices.py and routes/contingency.py
 * (Phase 3), and the ended-deal endpoints of Phase 5 in the same routes file
 * (backend/app/services/contingency/termination_record.py builds them).
 *
 * All calls go through APIAgent (cookie auth, structured `{code, message,
 * copy_key}` errors on `err.detail`).
 */

import { useCallback, useEffect, useState } from 'react';
import APIAgent from './apiAgent';

// ─── notice types, actions and error codes (mirrors app/services/contingency/wi_rules.py) ──

export const NOTICE_TYPES = {
  NOT_SATISFIED: 'not_satisfied',
  WAIVER: 'waiver',
  SELLER_CURE: 'seller_cure',
  SELLER_DECLINES_CURE: 'seller_declines_cure',
  LAND_DIVISION_APPROVED: 'land_division_approved',
  LAND_DIVISION_END: 'land_division_end',
};

/** Notice types that never take a statement (a waiver has fixed wording). */
export const STATEMENT_NOT_ALLOWED = [NOTICE_TYPES.WAIVER];
/** Notice types that never take attachments. */
export const ATTACHMENTS_NOT_ALLOWED = [
  NOTICE_TYPES.WAIVER,
  NOTICE_TYPES.SELLER_DECLINES_CURE,
  NOTICE_TYPES.LAND_DIVISION_END,
];

export const FLAG_ACTIONS = {
  OFFPLATFORM_NOTICE: 'offplatform_notice',
  CURE_DISPUTE: 'cure_dispute',
  LAND_DIVISION_DISPUTE: 'land_division_dispute',
  /** Copy §15.1: the buyer disagrees that the lead-based paint period doesn't apply. */
  NOT_APPLICABLE_DISPUTE: 'not_applicable_dispute',
};

/**
 * `property_transactions.termination_reason` for a deal an admin recorded as
 * ended by a document both parties signed (copy §15.2, `REASON_ADMIN_AGREEMENT`).
 */
export const TERMINATION_REASON_ADMIN_AGREEMENT = 'admin_record_agreement';

/** `method` values `POST …/offplatform-notices` accepts (notices.OFFPLATFORM_METHODS). */
export const OFFPLATFORM_METHODS = ['hand', 'mail', 'email', 'courier', 'other'];

export const ATTACHMENT_PURPOSES = { NOTICE: 'notice', OFFPLATFORM: 'offplatform' };

/** Upload limits (notices.ALLOWED_CONTENT_TYPES / MAX_UPLOAD_BYTES / MAX_ATTACHMENTS). */
export const ATTACHMENT_ACCEPT = 'application/pdf,image/jpeg,image/png';
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export const MAX_ATTACHMENTS = 10;

/** Error codes the hub handles specially (design §5.1 and the Phase 3 additions). */
export const NOTICE_ERROR_CODES = {
  DEADLINE_PASSED: 'deadline_passed',
  STALE_STATE: 'stale_state',
  PREVIEW_EXPIRED: 'preview_expired',
  PREVIEW_INVALID: 'preview_invalid',
  TRANSACTION_TERMINATED: 'transaction_terminated',
  NOTICE_ALREADY_DELIVERED: 'notice_already_delivered',
  CONTINGENCY_CLOSED: 'contingency_closed',
  /** Anvil H1: the row isn't a contingency selected in the signed offer (copy WI.error.notice_not_in_signed_offer). */
  CONTINGENCY_NOT_IN_SIGNED_OFFER: 'contingency_not_in_signed_offer',
  /** Anvil H1: the row carries a pre-tracking legacy status (copy WI.error.notice_legacy_status). */
  CONTINGENCY_LEGACY_STATUS: 'contingency_legacy_status',
  ATTACHMENT_REQUIRED: 'attachment_required',
  SIGNATURE_INVALID: 'signature_invalid',
  STATEMENT_REQUIRED: 'statement_required',
  NOTICES_UNAVAILABLE: 'notices_unavailable',
  NOTICE_TEMPLATES_UNAVAILABLE: 'notice_templates_unavailable',
  WRONG_PARTY: 'wrong_party',
  NOT_A_PARTY: 'not_a_party',
};

/**
 * The structured part of an API error: `{status, code, message, copyKey}`.
 * `message` is the backend's (already filled) legal copy when it sent one.
 *
 * @param {Error & {status?: number, detail?: any}} err
 */
export function noticeErrorInfo(err) {
  const detail = err && typeof err.detail === 'object' && err.detail !== null && !Array.isArray(err.detail)
    ? err.detail
    : null;
  return {
    status: err?.status ?? null,
    code: detail?.code ?? null,
    message: detail?.message || err?.message || 'Something went wrong. Nothing was delivered.',
    copyKey: detail?.copy_key ?? null,
    detail,
  };
}

// ─── notices (Phase 3) ─────────────────────────────────────────────────────

/**
 * `POST /api/contingencies/{id}/notices/preview` — the exact text, the
 * computed effect, warnings and a 15-minute preview token. Records nothing.
 *
 * @param {number} contingencyId
 * @param {{noticeType: string, statement?: string, attachmentIds?: number[], acknowledgeMissingAttachment?: boolean}} body
 */
export function previewNotice(contingencyId, { noticeType, statement, attachmentIds = [], acknowledgeMissingAttachment = false }) {
  const payload = {
    notice_type: noticeType,
    attachment_ids: attachmentIds,
    acknowledge_missing_attachment: Boolean(acknowledgeMissingAttachment),
  };
  if (statement && statement.trim()) payload.statement = statement;
  return APIAgent.request(`/api/contingencies/${contingencyId}/notices/preview`, 'POST', payload, {}, { timeout: 30000 });
}

/**
 * `POST /api/contingencies/{id}/notices` — sign and deliver a previewed notice.
 * Resolves `{notice, contingency, transaction_status, deal_ended, replayed,
 * confirmation, other_means?}`.
 *
 * @param {number} contingencyId
 * @param {{previewToken: string, typedName: string, attestationVersion: string, acknowledgeMissingAttachment?: boolean}} body
 */
export function deliverNotice(contingencyId, { previewToken, typedName, attestationVersion, acknowledgeMissingAttachment = false }) {
  return APIAgent.request(`/api/contingencies/${contingencyId}/notices`, 'POST', {
    preview_token: previewToken,
    signature: { typed_name: typedName, attestation_version: attestationVersion, consent: true },
    acknowledge_missing_attachment: Boolean(acknowledgeMissingAttachment),
  }, {}, { timeout: 60000 });
}

/**
 * `POST /api/contingencies/{id}/attachments` (multipart) — a report, test
 * result or a copy of an off-platform notice. Files can't be deleted later.
 * Resolves `{file_id, sha256, filename, size, content_type, ...}`.
 */
export function uploadContingencyAttachment(contingencyId, file, purpose = ATTACHMENT_PURPOSES.NOTICE) {
  const form = new FormData();
  form.append('file', file);
  form.append('purpose', purpose);
  return APIAgent.request(`/api/contingencies/${contingencyId}/attachments`, 'POST', form, {}, { timeout: 120000 });
}

/** `GET /api/contingency-attachments/{id}` as a download. */
export function downloadContingencyAttachment(attachmentId, filename = `attachment-${attachmentId}`) {
  return APIAgent.download(`/api/contingency-attachments/${attachmentId}`, filename);
}

/** `GET /api/contingency-notices/{id}` — the notice record. */
export function getContingencyNotice(noticeId) {
  return APIAgent.request(`/api/contingency-notices/${noticeId}`);
}

/** `GET /api/contingency-notices/{id}/document` — the notice PDF, as a download. */
export function downloadNoticeDocument(noticeId) {
  return APIAgent.download(`/api/contingency-notices/${noticeId}/document`, `contingency-notice-${noticeId}.pdf`);
}

/** `POST /api/contingency-notices/{id}/viewed` — the recipient's first view (204). */
export function markNoticeViewed(noticeId) {
  return APIAgent.request(`/api/contingency-notices/${noticeId}/viewed`, 'POST');
}

// ─── flags (Phase 3) ───────────────────────────────────────────────────────

/**
 * `POST /api/contingencies/{id}/offplatform-notices` — "I delivered a notice
 * another way". `deliveredOn` is a `YYYY-MM-DD` date.
 */
export function reportOffplatformNotice(contingencyId, { attachmentId, deliveredOn, method, noticeType, description }) {
  const payload = { attachment_id: attachmentId, delivered_on: deliveredOn, method };
  if (noticeType) payload.notice_type = noticeType;
  if (description && description.trim()) payload.description = description;
  return APIAgent.request(`/api/contingencies/${contingencyId}/offplatform-notices`, 'POST', payload);
}

/** `POST /api/contingencies/{id}/cure-dispute` — the buyer disputes a cure. Idempotent. */
export function disputeCure(contingencyId, statement) {
  return APIAgent.request(`/api/contingencies/${contingencyId}/cure-dispute`, 'POST',
    statement && statement.trim() ? { statement } : {});
}

/** `POST /api/contingencies/{id}/land-division-dispute` — the other party disputes an approval record. */
export function disputeLandDivision(contingencyId, statement) {
  return APIAgent.request(`/api/contingencies/${contingencyId}/land-division-dispute`, 'POST',
    statement && statement.trim() ? { statement } : {});
}

/**
 * `POST /api/contingencies/{id}/not-applicable-dispute` — the buyer disagrees
 * with the record that the lead-based paint period doesn't apply (copy §15.1).
 * Buyer only, once; flags the row and changes no status.
 */
export function disputeNotApplicable(contingencyId, statement) {
  return APIAgent.request(`/api/contingencies/${contingencyId}/not-applicable-dispute`, 'POST',
    statement && statement.trim() ? { statement } : {});
}

// ─── timeline ──────────────────────────────────────────────────────────────

/** `GET /api/transactions/{id}/contingency-events` — the timeline, oldest first. */
export function getContingencyEvents(transactionId) {
  return APIAgent.request(`/api/transactions/${transactionId}/contingency-events`);
}

// ─── termination and earnest money (Phase 5: design §4.1, §4.2) ────────────
//
// All three answer 404 `not_terminated` until the deal has ended, and 409
// `termination_pending` while a deal ended before Phase 5 is still winding
// down. The record and the panel are identical for both parties.

/** Error codes of the ended-deal endpoints. */
export const TERMINATION_ERROR_CODES = {
  NOT_TERMINATED: 'not_terminated',
  TERMINATION_PENDING: 'termination_pending',
  RECORD_UNAVAILABLE: 'termination_record_unavailable',
};

/**
 * `GET /api/transactions/{id}/termination` (`contract_tracking.terminated.termination_url`):
 *
 *   { transaction_id, transaction_status, offer_status,
 *     banner: {copy_key, text},
 *     record: { facts: {record_id, ended_at, reason, transition, contingency, notices, events, flags, ...},
 *               text: {title, intro, body, footer}|null, pdf_url },
 *     earnest_money: { title, intro, holder, amount_as_printed, quotes_heading,
 *                      quotes: [{kind, label: {copy_key, text}, text, found}], ... },
 *     relist_prompt: {copy_key, text, listing_id, automatic: false}|null }  // seller only
 */
export function getTransactionTermination(transactionId) {
  return APIAgent.request(`/api/transactions/${transactionId}/termination`);
}

/**
 * `GET /api/transactions/{id}/termination-record` — the factual PDF either
 * party can give the earnest-money holder (copy §7), as a download.
 */
export function downloadTerminationRecord(transactionId) {
  return APIAgent.download(`/api/transactions/${transactionId}/termination-record`,
    `termination-record-${transactionId}.pdf`);
}

/**
 * `GET /api/transactions/{id}/earnest-money` — the passages the neutral panel
 * quotes from the signed offer (copy §8), flat:
 *
 *   { holder_label: string|null,
 *     earnest_money_section_ref: string,       // e.g. "section 3"
 *     how_ref_e: string,                       // e.g. "section 6(e)"
 *     default_section_ref: string,             // e.g. "section 10"
 *     quotes: { earnest_money: string|null, contingency: string|null, default: string|null },
 *     release_paragraph_present: boolean,
 *     amount_as_printed: string|null,
 *     signed_text_available: boolean,          // false: cutthe6.com has no signed text to quote
 *     record_url: string }
 */
export function getEarnestMoneyPanel(transactionId) {
  return APIAgent.request(`/api/transactions/${transactionId}/earnest-money`);
}

// ─── hooks ─────────────────────────────────────────────────────────────────

/**
 * The contract timeline of one transaction, with `loading` / `error` / `data`.
 *
 * @param {number|null} transactionId
 * @param {*} [refreshKey] - Change it to reload (e.g. after a delivery).
 */
export function useContingencyEvents(transactionId, refreshKey) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getContingencyEvents(transactionId);
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load the timeline');
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return { events, loading, error, reload: load };
}

/**
 * The earnest-money panel data. `unavailable` is true on 404
 * `not_terminated` (the deal hasn't ended, e.g. a stale view), which is not
 * an error to show; a 409 `termination_pending` is shown as its message.
 */
export function useEarnestMoneyPanel(transactionId, enabled = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  const load = useCallback(async () => {
    if (!transactionId || !enabled) return;
    setLoading(true);
    setError(null);
    setUnavailable(false);
    try {
      setData(await getEarnestMoneyPanel(transactionId));
    } catch (err) {
      if (err?.status === 404) {
        setUnavailable(true);
        setData(null);
      } else {
        setError(err.message || 'Failed to load the earnest money details');
      }
    } finally {
      setLoading(false);
    }
  }, [transactionId, enabled]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, unavailable, reload: load };
}

/**
 * A one-shot async action with its own `loading` / `error` state. `run`
 * resolves `{ok: true, data}` or `{ok: false, error}` (never throws), where
 * `error` is {@link noticeErrorInfo}.
 */
export function useAsyncAction(fn) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fn(...args);
      return { ok: true, data };
    } catch (err) {
      const info = noticeErrorInfo(err);
      setError(info);
      return { ok: false, error: info };
    } finally {
      setLoading(false);
    }
  }, [fn]);

  const reset = useCallback(() => setError(null), []);

  return { run, loading, error, reset };
}
